import React, {useState, useCallback} from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications", { params: { limit: 25 } }); setItems(data || []); }
    catch { /* not logged in / transient */ }
  }, []);
  usePoll(load, 30000);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    const un = items.filter((n) => !n.read);
    if (!un.length) return;
    await Promise.allSettled(un.map((n) => api.post(`/notifications/${n.id}/read`)));
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="nav-notifications" aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"} className="relative inline-flex items-center justify-center min-h-11 min-w-11 text-muted-foreground hover:text-foreground transition-colors duration-300">
          {/* Badge anchors to the icon, not the (now 44px) tap target. */}
          <span className="relative inline-flex">
            <Bell className="h-4 w-4" strokeWidth={1} />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] leading-none bg-primary text-primary-foreground rounded-full">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 rounded-none border hairline bg-background">
        <div className="flex items-center justify-between px-4 py-3 border-b hairline">
          <span className="text-xs uppercase tracking-widest text-primary">Notifications</span>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <Check className="h-3 w-3" strokeWidth={1} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b hairline hover:bg-foreground/5 transition-colors ${n.read ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                  <div className={`min-w-0 ${n.read ? "pl-3.5" : ""}`}>
                    <div className="text-sm truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-muted-foreground truncate">{n.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
